import React, { useState, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { setIsLogin, setUserPermissionData, addLoginUser } from '../actions/Users';
import { useHistory, useLocation } from 'react-router-dom';
import { axiosNewInstance } from '../config/Api';
import toast from 'react-hot-toast';
import AuthWrapper from './AuthWrapper';

// Generate or retrieve a persistent device ID
const getDeviceId = () => {
    let deviceId = localStorage.getItem('lic_device_id');
    if (!deviceId) {
        deviceId = 'dev_' + Math.random().toString(36).substr(2, 9) + '_' + Date.now().toString(36);
        localStorage.setItem('lic_device_id', deviceId);
    }
    return deviceId;
};

const LoginCustomerForm = () => {
    const history = useHistory();
    const location = useLocation();
    const dispatch = useDispatch();

    // Step: 'username' | 'password' | 'otp'
    const [step, setStep] = useState('username');
    const [userId, setUserId] = useState('');
    const [password, setPassword] = useState('');
    const [message, setMessage] = useState('');
    const [messageType, setMessageType] = useState('');
    const [loading, setLoading] = useState(false);
    const [maskedMobile, setMaskedMobile] = useState('');
    const [backendUserId, setBackendUserId] = useState(null);

    // OTP
    const [otpDigits, setOtpDigits] = useState(['', '', '', '']);
    const [verifying, setVerifying] = useState(false);
    const ref0 = useRef(null);
    const ref1 = useRef(null);
    const ref2 = useRef(null);
    const ref3 = useRef(null);
    const inputRefs = [ref0, ref1, ref2, ref3];

    const handleOtpChange = (index, value) => {
        if (!/^\d*$/.test(value)) return;
        const newDigits = [...otpDigits];
        newDigits[index] = value.slice(-1);
        setOtpDigits(newDigits);
        if (value && index < 3) inputRefs[index + 1].current?.focus();
    };

    const handleOtpKeyDown = (index, e) => {
        if (e.key === 'Backspace' && !otpDigits[index] && index > 0) {
            inputRefs[index - 1].current?.focus();
        }
    };

    /* ------- Handle successful login response ------- */
    const handleLoginSuccess = (responseData) => {
        const token = responseData.token.token;
        localStorage.setItem('authToken', token);
        dispatch(setIsLogin(true));
        dispatch(setUserPermissionData(responseData.token?.permissions));
        const loggedInUser = responseData.token?.user;
        dispatch(addLoginUser(loggedInUser));
        toast.success('Login successful!');

        if (loggedInUser?.reset_password === 0) {
            history.push('/reset-password');
        } else {
            const redirectUrl =
                location?.state?.from ||
                (new URLSearchParams(location.search)).get('redirect') ||
                '/dashboard_admin';
            history.push(redirectUrl);
        }
    };

    /* ------- Step 1: Check if user exists ------- */
    const handleCheckUser = async (e) => {
        e.preventDefault();

        if (!userId) {
            setMessage('Username / Email / Mobile is required');
            setMessageType('danger');
            return;
        }

        setLoading(true);
        setMessage('');

        try {
            const response = await axiosNewInstance.post('/api-app/check-user', {
                user_id: userId,
                login_type: 'policyholder'
            });

            if (response.data.status === 1) {
                // User found — show password field
                setStep('password');
                setMessage('');
            }
        } catch (error) {
            if (error.response?.status === 404) {
                // User not found — if it looks like a mobile, try customerEnquiryLogin
                const cleanMobile = userId.replace(/\D/g, '');
                if (cleanMobile.length >= 10) {
                    try {
                        const customerResp = await axiosNewInstance.get(`/api/customerEnquiryLogin/${cleanMobile}`);
                        const customerData = customerResp.data;

                        // SAP returns an array of policyDetails
                        if (customerData && customerData.policyDetails && customerData.policyDetails.length > 0) {
                            const firstPolicy = customerData.policyDetails[0];
                            // Valid customer in SAP — redirect to register-customer with mobile and name pre-filled
                            history.push('/register-customer', {
                                mobile: cleanMobile,
                                customerName: firstPolicy.name, // The SAP field is 'name' inside policyDetails
                            });
                            return;
                        }
                    } catch (custErr) {
                        console.error('customerEnquiryLogin failed:', custErr);
                    }
                }
                setMessage('User not found.');
                setMessageType('danger');
            } else {
                const errMsg = error.response?.data?.message || 'Something went wrong.';
                setMessage(typeof errMsg === 'object' ? JSON.stringify(errMsg) : errMsg);
                setMessageType('danger');
            }
        } finally {
            setLoading(false);
        }
    };

    /* ------- Step 2: Submit credentials (password) ------- */
    const handleCredentialSubmit = async (e) => {
        e.preventDefault();

        if (!password) {
            setMessage('Password is required');
            setMessageType('danger');
            return;
        }

        setLoading(true);
        setMessage('');

        try {
            const response = await axiosNewInstance.post('/api-app/trusted-login', {
                user_id: userId,
                password: password,
                device_id: getDeviceId(),
                login_type: 'policyholder'
            });

            const data = response.data;

            if (data.status === 1) {
                handleLoginSuccess(data);
            } else if (data.status === 2) {
                setStep('otp');
                setMaskedMobile(data.masked_mobile);
                setBackendUserId(data.user_id);
                setMessage('OTP sent to ' + data.masked_mobile);
                setMessageType('success');
                setOtpDigits(['', '', '', '']);

                try {
                    await axiosNewInstance.post('/api/send-mobile-otp', {
                        user_id: data.user_id
                    });
                } catch (otpError) {
                    console.error("Failed to send OTP SMS:", otpError);
                }

            } else {
                setMessage(data.message || 'Login failed.');
                setMessageType('danger');
            }

        } catch (error) {
            const errMsg = error.response?.data?.message || 'Login failed.';
            setMessage(typeof errMsg === 'object' ? JSON.stringify(errMsg) : errMsg);
            setMessageType('danger');
        } finally {
            setLoading(false);
        }
    };

    /* ------- Step 3: Verify OTP ------- */
    const handleVerifyOtp = async (e) => {
        e.preventDefault();
        const otp = otpDigits.join('');
        if (otp.length !== 4) {
            setMessage('Please enter the 4-digit OTP.');
            setMessageType('danger');
            return;
        }
        setVerifying(true);
        setMessage('');

        try {
            const response = await axiosNewInstance.post('/api-app/verify-trusted-otp', {
                user_id: backendUserId,
                otp: otp,
                device_id: getDeviceId(),
            });

            const data = response.data;

            if (data.status === 1) {
                handleLoginSuccess(data);
            } else {
                setMessage(data.message || 'OTP verification failed.');
                setMessageType('danger');
            }

        } catch (error) {
            const errMsg = error.response?.data?.message || 'Failed to verify OTP.';
            setMessage(typeof errMsg === 'object' ? JSON.stringify(errMsg) : errMsg);
            setMessageType('danger');
        } finally {
            setVerifying(false);
        }
    };

    return (
        <div className="row">
            {message && (
                <div className="col-sm-12">
                    <div className={`alert alert-${messageType} alert-dismissible fade show`} role="alert">
                        {message}
                        <button type="button" className="close" onClick={() => setMessage('')}>
                            <span aria-hidden="true">&times;</span>
                        </button>
                    </div>
                </div>
            )}

            <div className="col-sm-12">
                {step === 'username' && (
                    <form onSubmit={handleCheckUser}>
                        <input
                            type="text"
                            placeholder="Username / Email / Mobile"
                            className="form-control"
                            value={userId}
                            onChange={(e) => setUserId(e.target.value)}
                            style={{ marginBottom: '15px' }}
                            autoFocus
                        />
                        <button
                            type="submit"
                            className="btn btn-primary btn-block"
                            disabled={loading}
                            style={{ marginTop: '10px' }}
                        >
                            {loading ? 'Checking...' : 'Next'}
                        </button>
                    </form>
                )}

                {step === 'password' && (
                    <form onSubmit={handleCredentialSubmit}>
                        <input
                            type="text"
                            className="form-control"
                            value={userId}
                            readOnly
                            style={{ marginBottom: '15px', backgroundColor: '#e9ecef' }}
                        />
                        <input
                            type="password"
                            placeholder="Password"
                            className="form-control"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            autoFocus
                        />
                        <button
                            type="submit"
                            className="btn btn-primary btn-block"
                            disabled={loading}
                            style={{ marginTop: '20px' }}
                        >
                            {loading ? 'Logging in...' : 'Login'}
                        </button>
                        <button
                            type="button"
                            className="btn btn-link btn-block"
                            onClick={() => history.push('/forgot-pass-new', { mobile: userId, login_type: 'policyholder' })}
                            style={{ marginTop: '5px', color: '#dc3545' }}
                        >
                            Forgot Password?
                        </button>
                        <button
                            type="button"
                            className="btn btn-link btn-block"
                            onClick={() => { setStep('username'); setPassword(''); setMessage(''); }}
                            style={{ marginTop: '5px' }}
                        >
                            ← Change User
                        </button>
                    </form>
                )}

                {step === 'otp' && (
                    <form onSubmit={handleVerifyOtp}>
                        <p style={{ marginBottom: '15px', color: '#666', textAlign: 'center' }}>
                            Enter the 4-digit OTP sent to {maskedMobile}
                        </p>
                        <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginBottom: '20px' }}>
                            {[0, 1, 2, 3].map((index) => (
                                <input
                                    key={index}
                                    ref={inputRefs[index]}
                                    type="text"
                                    inputMode="numeric"
                                    maxLength={1}
                                    className="form-control"
                                    style={{ width: '55px', textAlign: 'center', fontSize: '1.5rem' }}
                                    value={otpDigits[index]}
                                    onChange={(e) => handleOtpChange(index, e.target.value)}
                                    onKeyDown={(e) => handleOtpKeyDown(index, e)}
                                />
                            ))}
                        </div>
                        <button
                            type="submit"
                            className="btn btn-primary btn-block"
                            disabled={verifying}
                            style={{ marginTop: '10px' }}
                        >
                            {verifying ? 'Verifying...' : 'Verify OTP'}
                        </button>
                        <button
                            type="button"
                            className="btn btn-link btn-block"
                            onClick={() => { setStep('username'); setPassword(''); setMessage(''); setOtpDigits(['', '', '', '']); }}
                            style={{ marginTop: '10px' }}
                        >
                            ← Back to Login
                        </button>
                    </form>
                )}
            </div>
        </div>
    );
};

const LoginCustomer = () => {
    const meta = {
        meta_title: 'Policy Holder Login',
        meta_description: 'Login to Policy Holder portal',
        meta_keywords: 'login, policy holder, customer',
    };

    const breadcrumbs = [
        { title: "Policy Holder Login", url: "/login-customer" },
        { title: "Home", url: "/" }
    ];

    return (
        <AuthWrapper title="Policy Holder Login" meta={meta} breadcrumbs={breadcrumbs} activeTab="policyholder">
            <LoginCustomerForm />
        </AuthWrapper>
    );
};

export default LoginCustomer;
